import { useGameStore } from '@/react/state/gameStore'
import { CanonicalCut } from '@/core/cuts'

export type { CanonicalCut }

export interface CutHistoryItem {
  cutNumber: number
  cut: CanonicalCut
  cutType: string
  volumeReduction: number
  objectiveValueReduction: number
  originalVolume: number
  newVolume: number
  originalObjectiveValue: number
  newObjectiveValue: number
  optimalSolution: number[]
  timestamp: Date
}

class UIManager {
  constructor() {
    // Store-backed UI bridge for engine controllers.
  }

  showLoading(isLoading: boolean) {
    useGameStore.getState().setLoading(isLoading)
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    useGameStore.getState().addNotification(message, type)
  }

  updateStats(cutsRemaining: number, volumeReduction: number, score: number) {
    useGameStore.getState().updateStats({
      cutsRemaining,
      volumeReduction,
      currentScore: score,
    })
  }

  createCutButtons(cuts: CanonicalCut[]) {
    useGameStore.getState().setAvailableCuts(cuts)
  }

  updateCutsHistoryPanel(history: CutHistoryItem[], initialLP?: number, initialIP?: number, initialIPSol?: number[]): void {
    void initialIPSol
    const store = useGameStore.getState()
    store.setCutsHistory(history)
    store.updateStats({
      initialObjectiveValue: initialLP ?? store.game.stats.initialObjectiveValue,
      initialIPOptimalValue: initialIP ?? store.game.stats.initialIPOptimalValue,
    })
  }

  disableCutButtons() {
    // no-op compatibility hook
  }

  hideCutButtons() {
    // no-op compatibility hook
  }

  showCutConfirmation(_onConfirm: () => void, _onCancel: () => void) {
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_PREVIEW_READY' })
  }

  hideCutConfirmationButtons() {
    // no-op compatibility hook
  }

  initializeSimplexCutInteraction(
    _cutIndex: number,
    initialMu: number,
    _onMuChange: (mu: number) => void,
    _onConfirm: () => void,
    _onCancel: () => void,
  ) {
    useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_MU_ADJUST_REQUIRED', mu: initialMu })
  }

  showGameOverModal(finalScore: number, volumeReduction: number) {
    void finalScore
    void volumeReduction
    useGameStore.getState().setMode('gameOver')
    useGameStore.getState().showModal('gameOver')
  }

  clearUserCuts() {
    // Handled by setAvailableCuts usually
  }

  reinitializePanelToggles() {}

  minimizeAllPanels() {
    const store = useGameStore.getState()
    store.setPanelState('math', 'minimized')
    store.setPanelState('cut', 'minimized')
    store.setPanelState('history', 'minimized')
    store.setPanelState('bnb', 'minimized')
  }

  expandAllPanels() {
    const store = useGameStore.getState()
    store.setPanelState('math', 'expanded')
    store.setPanelState('cut', 'expanded')
    store.setPanelState('history', 'expanded')
    store.setPanelState('bnb', 'expanded')
  }

  initializeCutsLimitConfiguration(currentLimit: number, onLimitChange: (n: number) => void) {
    void currentLimit
    useGameStore.getState().setCutsLimitCallback(onLimitChange)
  }

  static triggerLimitChange(newLimit: number) {
    const cb = useGameStore.getState().cutsLimitCallback
    if (cb) cb(newLimit)
  }
}

export const uiManager = new UIManager()
